import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  BiUpload,
  BiFolder,
  BiFolderOpen,
  BiFile,
  BiPlus,
  BiMove,
  BiChevronRight,
  BiChevronDown,
  BiHome,
} from "react-icons/bi";
import { AiOutlineEye, AiOutlineDelete } from "react-icons/ai";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { useUpload } from "../../context/UploadContext";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal/DeleteConfirmationModal";
import "./RenderMyDocs.scss";
import {
  createFile,
  createFolder,
  getDirectoryTree,
  deleteDocument,
  moveDocument,
  getMimeTypeFromFilename,
  getFileSizeFromResponse,
} from "../../services/api/AddDocApi";
import { uploadFileWithProgress } from "../../utils/ftpUploadUtils";
import { useStorage } from "../../context/StorageContext";

export const RenderMyDocs = ({
  mydocs,
  selectedCourtType,
  courtRef,
  refreshDocs,
}) => {
  // ==================== UTILITY FUNCTIONS ====================

  // Maximum file size in bytes (50MB)
  const MAX_FILE_SIZE = 50 * 1024 * 1024;

  // Allowed file types with their MIME types and extensions
  const ALLOWED_FILE_TYPES = {
    'application/pdf': { ext: '.pdf', name: 'PDF' },
    'application/msword': { ext: '.doc', name: 'Word Document' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { ext: '.docx', name: 'Word Document' },
    'text/plain': { ext: '.txt', name: 'Text File' },
    'image/png': { ext: '.png', name: 'PNG Image' },
    'image/jpeg': { ext: '.jpg,.jpeg', name: 'JPEG Image' },
    'application/vnd.ms-excel': { ext: '.xls', name: 'Excel Spreadsheet' },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { ext: '.xlsx', name: 'Excel Spreadsheet' }
  };

  /**
   * Get the accept string for file input
   */
  const getAcceptString = () => {
    return '.pdf,.doc,.docx,.txt,.png,.jpeg,.jpg,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,image/png,image/jpeg,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  };

  /**
   * Validate file type and size
   */
  const validateFileType = (file) => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'image/png',
      'image/jpeg',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.png', '.jpg', '.jpeg', '.xls', '.xlsx'];

    if (!file) {
      return {
        isValid: false,
        errorType: 'NO_FILE',
        error: 'No file provided'
      };
    }

    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      return {
        isValid: false,
        errorType: 'INVALID_TYPE',
        error: file.type || fileExtension
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        isValid: false,
        errorType: 'FILE_TOO_LARGE',
        error: file.size
      };
    }

    return { isValid: true };
  };

  /**
   * Get error message based on error type
   */
  const getErrorMessage = (errorType, error) => {
    switch (errorType) {
      case 'NO_FILE':
        return 'Please select a file to upload';
      case 'INVALID_TYPE':
        return `Invalid file type. Allowed formats: PDF, DOC, DOCX, TXT, PNG, JPEG, JPG, XLS, XLSX`;
      case 'FILE_TOO_LARGE':
        const sizeMB = (error / (1024 * 1024)).toFixed(2);
        return `File size (${sizeMB}MB) exceeds the maximum limit of 50MB`;
      default:
        return 'An error occurred while validating the file';
    }
  };

  /**
   * Check if file is an Excel spreadsheet
   */
  const isExcelFile = (mimeType, filename = '') => {
    const excelMimeTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const hasExcelMimeType = excelMimeTypes.includes(mimeType);
    const hasExcelExtension = filename.toLowerCase().endsWith('.xls') ||
      filename.toLowerCase().endsWith('.xlsx');

    return hasExcelMimeType || hasExcelExtension;
  };

  /**
   * Format file size to human-readable format
   */
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 KB";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  /**
   * Extract filename from URL
   */
  const extractFilenameFromUrl = (url) => {
    if (!url) return "untitled";
    try {
      const urlParts = url.split("/");
      const lastPart = urlParts[urlParts.length - 1];
      const decodedFilename = decodeURIComponent(lastPart.split("?")[0]);
      const cleanedFilename = decodedFilename.replace(/^\d+-/, "");

      if (cleanedFilename && cleanedFilename.length > 0) {
        return cleanedFilename;
      }

      return "Document";
    } catch (error) {
      console.error("Error extracting filename:", error);
      return "Document";
    }
  };

  /**
   * Get file icon based on type
   */
  const getFileIcon = (item, isExpanded = false) => {
    if (item.type === "folder") {
      return isExpanded ? (
        <BiFolderOpen className="file-icon folder-icon" size={20} />
      ) : (
        <BiFolder className="file-icon folder-icon" size={20} />
      );
    }

    const mimeType = item.mimeType || "";
    const fileName = item.name || "";

    if (mimeType.includes("pdf")) {
      return <BiFile className="file-icon pdf-icon" size={20} />;
    } else if (mimeType.includes("image")) {
      return <BiFile className="file-icon image-icon" size={20} />;
    } else if (mimeType.includes("word") || mimeType.includes("document")) {
      return <BiFile className="file-icon doc-icon" size={20} />;
    } else if (
      mimeType.includes("sheet") ||
      mimeType.includes("excel") ||
      mimeType.includes("spreadsheet") ||
      fileName.endsWith('.xls') ||
      fileName.endsWith('.xlsx')
    ) {
      return <BiFile className="file-icon excel-icon" size={20} />;
    }
    return <BiFile className="file-icon default-icon" size={20} />;
  };

  /**
   * Get file preview (thumbnail or icon)
   */
  const getFilePreview = (item, isExpanded = false) => {
    if (item.type === "folder") {
      return (
        <div className="file-preview-container folder">
          {isExpanded ? (
            <BiFolderOpen className="file-icon folder-icon" size={20} />
          ) : (
            <BiFolder className="file-icon folder-icon" size={20} />
          )}
        </div>
      );
    }

    const mimeType = item.mimeType || "";

    // Show image thumbnails for image files
    if (mimeType.includes("image") && item.url) {
      return (
        <div className="file-preview-container image">
          <img
            src={item.url}
            alt={item.name}
            className="image-thumbnail"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "block";
            }}
          />
          <BiFile
            className="file-icon image-icon fallback-icon"
            size={20}
            style={{ display: "none" }}
          />
        </div>
      );
    }

    // Show appropriate icons for other file types
    return (
      <div className="file-preview-container file">
        {mimeType.includes("pdf") ? (
          <BiFile className="file-icon pdf-icon" size={20} />
        ) : mimeType.includes("word") || mimeType.includes("document") ? (
          <BiFile className="file-icon doc-icon" size={20} />
        ) : mimeType.includes("sheet") ||
          mimeType.includes("excel") ||
          mimeType.includes("spreadsheet") ||
          item.name?.endsWith('.xls') ||
          item.name?.endsWith('.xlsx') ? (
          <BiFile className="file-icon excel-icon" size={20} />
        ) : (
          <BiFile className="file-icon default-icon" size={20} />
        )}
      </div>
    );
  };

  /**
   * Get clean display name
   */
  const getDisplayName = (item) => {
    if (item.type === "folder") {
      return item.name;
    }

    if (item.url) {
      const cleanName = extractFilenameFromUrl(item.url);
      if (cleanName && cleanName !== "Document" && cleanName !== "untitled") {
        return cleanName;
      }
    }

    if (item.name && item.name.includes("%")) {
      try {
        const decoded = decodeURIComponent(item.name);
        const filename = decoded.split("/").pop() || decoded;
        const cleanedFilename = filename.replace(/^\d+-/, "");
        return cleanedFilename || item.name;
      } catch (error) {
        return item.name;
      }
    }

    if (item.name) {
      return item.name.replace(/^\d+-/, "");
    }

    return "Untitled Document";
  };

  /**
   * Format date to locale string
   */
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  /**
   * Find folder by ID in the tree
   */
  const findFolderById = (tree, folderId) => {
    for (const item of tree) {
      if (item._id === folderId) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = findFolderById(item.children, folderId);
        if (found) return found;
      }
    }
    return null;
  };

  // ==================== STATE MANAGEMENT ====================

  const [expandedFolders, setExpandedFolders] = useState(new Set(["root"]));
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState("file");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    docId: null,
    docName: "",
  });
  const [moveModal, setMoveModal] = useState({ isOpen: false, document: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [folderName, setFolderName] = useState("");
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });

  // Background upload tracking from context (persists across all page navigation)
  const {
    backgroundUploads,
    setBackgroundUploads,
    uploadPromisesRef,
    removeBackgroundUpload,
    clearCompletedUploads
  } = useUpload();

  const createModalRef = useRef(null);
  const isMounted = useRef(true); // Track mount status
  const { showToast } = useToast();
  const { role, company } = useAuth();
  const { refreshStorage } = useStorage(); 

  // ==================== EFFECTS ====================

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    // Prevent closing if batch upload is in progress (only if mounted)
    if (batchProgress.total > 0 && batchProgress.current < batchProgress.total) {
      showToast("Uploading in background...", "info");
      // Allow closing, as "fire and forget" implies we can leave
      // But we reset local state
    }
    
    setShowCreateModal(false);
    setUploadedUrl("");
    setFolderName("");
    setSelectedFile(null);
    setUploading(false);
    setUploadProgress(0);
    setBatchProgress({ current: 0, total: 0 });
  }, [batchProgress, showToast]);

  const handleCreateFile = useCallback(async () => {
    if (!uploadedUrl) {
      showToast("Please upload a document first!", "error");
      return;
    }

    if (!courtRef) {
      showToast("Court reference is required!", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const filename = selectedFile
        ? selectedFile.name
        : extractFilenameFromUrl(uploadedUrl);
      const payload = {
        name: filename,
        courtRef: courtRef,
        url: `${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/view/${uploadedUrl}`,
        parentId: currentFolderId,
        size: selectedFile ? selectedFile.size : 0,
        mimeType: selectedFile
          ? selectedFile.type
          : getMimeTypeFromFilename(filename),
        metadata: {
          uploadedAt: new Date().toISOString(),
        },
      };

      await createFile(payload);
      showToast("File uploaded successfully!", "success");
      handleCloseCreateModal();
      if (typeof refreshDocs === "function") refreshDocs();
      refreshStorage?.();
    } catch (error) {
      console.error("Error creating file:", error);
      showToast("Failed to upload file!", "error");
    } finally {
      if (isMounted.current) setIsSubmitting(false);
    }
  }, [uploadedUrl, courtRef, selectedFile, currentFolderId, handleCloseCreateModal, refreshDocs, showToast, refreshStorage ]);

  const handleCreateFolder = useCallback(async () => {
    if (!folderName.trim()) {
      showToast("Please enter a folder name!", "error");
      return;
    }

    if (!courtRef) {
      showToast("Court reference is required!", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: folderName.trim(),
        courtRef: courtRef,
        parentId: currentFolderId,
      };

      await createFolder(payload);
      showToast("Folder created successfully!", "success");
      handleCloseCreateModal();
      if (typeof refreshDocs === "function") {
        refreshDocs();
      }
    } catch (error) {
      console.error("Error creating folder:", error);
      showToast(error.message || "Failed to create folder!", "error");
    } finally {
      if (isMounted.current) setIsSubmitting(false);
    }
  }, [folderName, courtRef, currentFolderId, handleCloseCreateModal, refreshDocs, showToast]);

  useEffect(() => {
    const handleModalClickOutside = (event) => {
      if (
        showCreateModal &&
        createModalRef.current &&
        !createModalRef.current.contains(event.target)
      ) {
        handleCloseCreateModal();
      }
    };

    const handleKeyDown = (event) => {
      if (showCreateModal && event.key === 'Enter' && createType === "folder") {
        event.preventDefault();

        // Check if button should be disabled for folder creation
        const isDisabled = isSubmitting || !folderName.trim();

        if (!isDisabled) {
          handleCreateFolder();
        }
      }
    };

    if (showCreateModal) {
      document.addEventListener("mousedown", handleModalClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleModalClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showCreateModal, handleCloseCreateModal, isSubmitting, createType, folderName, handleCreateFolder]);

  // Track previous upload statuses to detect changes
  const prevUploadsRef = useRef([]);

  // Check for completed uploads and refresh when status changes
  useEffect(() => {
    const prevUploads = prevUploadsRef.current;

    // Find uploads that just transitioned from 'uploading' to 'completed'
    const newlyCompletedUploads = backgroundUploads.filter(upload => {
      const prevUpload = prevUploads.find(prev => prev.id === upload.id);
      return upload.status === 'completed' &&
        prevUpload?.status === 'uploading';
    });

    // Also check for uploads that were already completed but not yet refreshed (on mount)
    const unrefreshedCompleted = backgroundUploads.filter(
      upload => upload.status === 'completed' && !upload.refreshed
    );

    const needsRefresh = newlyCompletedUploads.length > 0 ||
      (unrefreshedCompleted.length > 0 && prevUploads.length === 0);

    if (needsRefresh && typeof refreshDocs === 'function') {

      // Mark these uploads as refreshed
      setBackgroundUploads(prev =>
        prev.map(u => {
          const isNewlyCompleted = newlyCompletedUploads.some(nc => nc.id === u.id);
          const isUnrefreshed = unrefreshedCompleted.some(uc => uc.id === u.id);

          if ((isNewlyCompleted || isUnrefreshed) && u.status === 'completed') {
            return { ...u, refreshed: true };
          }
          return u;
        })
      );

      refreshDocs();
    }

    // Update ref for next comparison
    prevUploadsRef.current = backgroundUploads.map(u => ({ id: u.id, status: u.status }));
  }, [backgroundUploads, refreshDocs, setBackgroundUploads]);

  // ==================== HANDLERS ====================

  const directoryTree = mydocs?.myDocuments || [];

  const toggleFolder = (folderId) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const handleItemClick = (item, parentId = null) => {
    setSelectedItem(item._id);
    if (item.type === "folder") {
      toggleFolder(item._id);
      setCurrentFolderId(item._id);
    } else {
      setCurrentFolderId(parentId);
    }
  };

  const handlePreview = (url) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  const handleDelete = (docId, docName = "document") => {
    setDeleteModal({ isOpen: true, docId, docName });
    if (typeof refreshDocs === "function") refreshDocs();
      refreshStorage?.();
  };

  const confirmDelete = async () => {
    try {
      await deleteDocument(deleteModal.docId);
      showToast("Document deleted successfully!", "success");
      if (currentFolderId === deleteModal.docId) {
        const breadcrumbPath = getBreadcrumbPath();
        if (breadcrumbPath.length > 1) {
          // Go to parent folder (second to last in breadcrumb)
          const parentFolder = breadcrumbPath[breadcrumbPath.length - 2];
          setCurrentFolderId(parentFolder._id);
          setSelectedItem(parentFolder._id);
        } else {
          // If no parent, go to root
          setCurrentFolderId(null);
          setSelectedItem(null);
        }
      }

      setDeleteModal({ isOpen: false, docId: null, docName: "" });

      if (typeof refreshDocs === "function") {
        refreshDocs();
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      showToast("Failed to delete document!", "error");
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, docId: null, docName: "" });
  };

  // removeBackgroundUpload and clearCompletedUploads now come from props

  const handleMove = (document) => {
    setMoveModal({ isOpen: true, document });
  };

  const handleMoveComplete = async (targetFolderId) => {
    try {
      await moveDocument(moveModal.document._id, targetFolderId);
      showToast("Document moved successfully!", "success");
      setMoveModal({ isOpen: false, document: null });
      if (typeof refreshDocs === "function") {
        refreshDocs();
      }
    } catch (error) {
      console.error("Error moving document:", error);
      showToast("Failed to move document!", "error");
    }
  };

  const handleFileUpload = async (file, isBatch = false) => {
    if (!file) return;

    // Generate unique upload ID
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    if (!isBatch && isMounted.current) {
      setUploading(true);
      setUploadProgress(0);
      setSelectedFile(file);
    }

    // Add to background uploads tracking
    const uploadEntry = {
      id: uploadId,
      fileName: file.name,
      fileSize: file.size,
      progress: 0,
      status: 'uploading', // uploading, completed, error
      startTime: Date.now()
    };

    setBackgroundUploads(prev => [...prev, uploadEntry]);

    try {
      const filePath = company?._id ? `/${company._id}/mydocs` : `/unknown`;

      // Start the upload promise and store it in ref
      const uploadPromise = uploadFileWithProgress(
        file,
        filePath,
        (progress) => {
          if (!isBatch && isMounted.current) setUploadProgress(progress);
          // Update background upload progress
          setBackgroundUploads(prev =>
            prev.map(u => u.id === uploadId ? { ...u, progress } : u)
          );
        },
        async (result) => {
          if (!isBatch && isMounted.current) {
            setUploadedUrl(result.imageUrl);
            setUploading(false);
          }

          // Mark as completed in background uploads
          setBackgroundUploads(prev =>
            prev.map(u => u.id === uploadId
              ? { ...u, status: 'completed', progress: 100, result, completionTime: Date.now() }
              : u
            )
          );

          if (!isBatch) showToast("File uploaded successfully!", "success");

          // Auto-save the file to database after upload completes
          try {
            const filename = file.name;
            const payload = {
              name: filename,
              courtRef: courtRef,
              url: `${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/view/${result.imageUrl}`,
              parentId: currentFolderId,
              size: file.size,
              mimeType: file.type,
              metadata: {
                uploadedAt: new Date().toISOString(),
              },
            };

            await createFile(payload);
            if (!isBatch) showToast("Document saved successfully!", "success");

            // Refresh the document list - Safe to call even if unmounted?
            // Usually fine, but better to check
            if (typeof refreshDocs === "function" && isMounted.current) {
              refreshDocs();
            }
            refreshStorage?.()
          } catch (saveError) {
            console.error("Error saving file to database:", saveError);
            showToast("Upload successful but failed to save document!", "error");
          }
        },
        (error) => {
          console.error("File upload failed:", error);
          if (!isBatch && isMounted.current) {
            setUploading(false);
            setUploadProgress(0);
            setSelectedFile(null);
          }

          // Mark as error in background uploads
          setBackgroundUploads(prev =>
            prev.map(u => u.id === uploadId
              ? { ...u, status: 'error', error: error.message, completionTime: Date.now() }
              : u
            )
          );

          showToast(error.message || "File upload failed", "error");
        }
      );

      // Store the promise reference so it continues in background
      uploadPromisesRef.current[uploadId] = uploadPromise;

      // Await the promise
      await uploadPromise;

    } catch (error) {
      console.error("File upload failed:", error);
      if (!isBatch && isMounted.current) {
        setUploading(false);
        setUploadProgress(0);
        setSelectedFile(null);
      }

      // Mark as error in background uploads
      setBackgroundUploads(prev =>
        prev.map(u => u.id === uploadId
          ? { ...u, status: 'error', error: error.message, completionTime: Date.now() }
          : u
        )
      );

      showToast(error.message || "File upload failed", "error");
    } finally {
      // Clean up the promise reference after completion
      delete uploadPromisesRef.current[uploadId];
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate all files first
    const validFiles = [];
    for (const file of files) {
      const validation = validateFileType(file);
      if (!validation.isValid) {
        showToast(`File ${file.name}: ${getErrorMessage(validation.errorType, validation.error)}`, "error");
      } else {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) {
      e.target.value = "";
      return;
    }

    setBatchProgress({ current: 0, total: validFiles.length });
    setUploading(true); // Flag global uploading state

    // Fire all uploads in parallel for true "fire and forget"
    // The promises will persist in background even if component unmounts
    const uploadPromises = validFiles.map(async (file) => {
       try {
         await handleFileUpload(file, true);
         // Only update local state if still mounted
         if (isMounted.current) {
           setBatchProgress(prev => ({ ...prev, current: prev.current + 1 }));
         }
       } catch (error) {
         console.error("Batch item failed", error);
       }
    });

    // Handle completion of the whole batch for UI cleanup
    Promise.allSettled(uploadPromises).then(() => {
      showToast(`Finished processing ${validFiles.length} file(s).`, "success");
        if (isMounted.current) {
            setUploading(false);
            // Close modal after short delay
            setTimeout(() => {
                if (isMounted.current) {
                    setBatchProgress({ current: 0, total: 0 });
                    handleCloseCreateModal();
                }
            }, 1000);
        }
    });
    
    e.target.value = "";
  };

  const handleCancelUpload = () => {
    setSelectedFile(null);
    setUploadedUrl("");
    setUploadProgress(0);
    setUploading(false);
    setBatchProgress({ current: 0, total: 0 });
  };

  const handleDownload = async (url, filename = null) => {
    try {
      showToast("Downloading file...", "info");

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;

      if (filename) {
        link.download = filename;
      } else {
        const urlParts = url.split("/");
        const lastPart = urlParts[urlParts.length - 1];
        const cleanFilename = lastPart.split("?")[0];
        link.download = cleanFilename || "document";
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      showToast("File downloaded successfully!", "success");
    } catch (error) {
      console.error("Download error:", error);
      showToast("Failed to download file. Opening in new tab.", "error");
      window.open(url, "_blank");
    }
  };

  const getCurrentFolderName = () => {
    if (!currentFolderId) {
      return "Root";
    }

    const folder = findFolderById(directoryTree, currentFolderId);
    return folder ? folder.name : "Unknown";
  };

  const getBreadcrumbPath = () => {
    if (!currentFolderId) {
      return [];
    }

    const findPath = (tree, targetId, currentPath = []) => {
      for (const item of tree) {
        const newPath = [...currentPath, item];
        if (item._id === targetId) {
          return newPath;
        }
        if (item.children && item.children.length > 0) {
          const found = findPath(item.children, targetId, newPath);
          if (found) return found;
        }
      }
      return null;
    };

    const foundPath = findPath(directoryTree, currentFolderId);
    return foundPath || [];
  };

  const handleRootClick = () => {
    setCurrentFolderId(null);
    setSelectedItem(null);
  };

  const handleFolderClick = (folderId) => {
    setCurrentFolderId(folderId);
    setSelectedItem(folderId);
    // Ensure the folder is expanded
    if (!expandedFolders.has(folderId)) {
      const newExpanded = new Set(expandedFolders);
      newExpanded.add(folderId);
      setExpandedFolders(newExpanded);
    }
  };

  // ==================== RENDER FUNCTIONS ====================

  const renderTreeItems = (items, level = 0, parentId = null) => {
    if (!items || items.length === 0) return null;

    return items.map((item) => {
      const isExpanded = expandedFolders.has(item._id);
      const isSelected = selectedItem === item._id;
      const hasChildren = item.children && item.children.length > 0;

      return (
        <div key={item._id} className="tree-item">
          <div
            className={`tree-item-content ${isSelected ? "selected" : ""} ${item.type
              }`}
            style={{ paddingLeft: `${level * 20 + 8}px` }}
            onClick={() => handleItemClick(item, parentId)}
            onDoubleClick={() =>
              item.type === "file" && handlePreview(item.url)
            }
          >
            <div className="tree-item-left">
              {item.type === "folder" ? (
                <span className="expand-icon">
                  {hasChildren ? (
                    isExpanded ? (
                      <BiChevronDown size={16} />
                    ) : (
                      <BiChevronRight size={16} />
                    )
                  ) : (
                    <span
                      style={{ width: "16px", display: "inline-block" }}
                    ></span>
                  )}
                </span>
              ) : (
                <span style={{ width: "16px", display: "inline-block" }}></span>
              )}

              {getFilePreview(item, isExpanded)}

              <span className="item-name" title={getDisplayName(item)}>
                {getDisplayName(item)}
              </span>
            </div>

            <div className="tree-item-right">
              <span className="item-date">{formatDate(item.createdAt)}</span>
              <span className="item-size">
                {item.type === "folder"
                  ? `${item.children?.length || 0} items`
                  : formatFileSize(item.size)}
              </span>

              <div className="item-actions">
                {item.type === "file" && (
                  <button
                    className="action-btn download-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(item.url, item.name);
                    }}
                    title="Download"
                  >
                    <BiUpload
                      size={16}
                      style={{ transform: "rotate(180deg)" }}
                    />
                  </button>
                )}

                <button
                  className="action-btn delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item._id, item.name);
                  }}
                  title="Delete"
                >
                  <AiOutlineDelete size={16} />
                </button>
              </div>
            </div>
          </div>

          {item.type === "folder" && isExpanded && hasChildren && (
            <div className="tree-children">
              {renderTreeItems(item.children, level + 1, item._id)}
            </div>
          )}
        </div>
      );
    });
  };

  // ==================== MAIN RENDER ====================

  if (!mydocs || !mydocs.myDocuments) {
    return (
      <div className="my-docs-container loading">Loading documents...</div>
    );
  }

  return (
    <div className="my-docs-container hierarchy">
      {/* Background Uploads Notification */}
      {backgroundUploads.length > 0 && (
        <div className="background-uploads-notification">
          <div className="uploads-header">
            <div className="uploads-title">
              <BiFile size={16} />
              <span>
                {backgroundUploads.filter(u => u.status === 'uploading').length > 0
                  ? `Uploading ${backgroundUploads.filter(u => u.status === 'uploading').length} file(s)...`
                  : 'Uploads'}
              </span>
            </div>
            {backgroundUploads.some(u => u.status !== 'uploading') && (
              <button
                className="clear-uploads-btn"
                onClick={clearCompletedUploads}
                title="Clear completed/failed"
              >
                Clear
              </button>
            )}
          </div>
          <div className="uploads-list">
            {backgroundUploads.map(upload => (
              <div key={upload.id} className={`upload-item ${upload.status}`}>
                <div className="upload-info">
                  <div className="upload-name">{upload.fileName}</div>
                  {upload.status === 'uploading' && (
                    <div className="upload-progress-bar">
                      <div
                        className="upload-progress-fill"
                        style={{ width: `${upload.progress}%` }}
                      />
                      <span className="upload-progress-text">
                        {Math.round(upload.progress)}%
                      </span>
                    </div>
                  )}
                  {upload.status === 'completed' && (
                    <div className="upload-status success">✓ Completed</div>
                  )}
                  {upload.status === 'error' && (
                    <div className="upload-status error">✗ {upload.error || 'Failed'}</div>
                  )}
                </div>
                <button
                  className="remove-upload-btn"
                  onClick={() => removeBackgroundUpload(upload.id)}
                  title="Remove"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="my-docs-header">
        <div className="header-top">
          <h3>My Documents</h3>
          <div className="header-actions">
            <button
              className="add-doc-btn"
              onClick={() => {
                setCreateType("folder");
                setShowCreateModal(true);
              }}
            >
              <BiPlus /> New Folder
            </button>
            <button
              className="add-doc-btn primary"
              onClick={() => {
                setCreateType("file");
                setShowCreateModal(true);
              }}
            >
              <BiPlus /> Add Document
            </button>
          </div>
        </div>

        {/* Info bar with breadcrumb */}
        <div className="info-bar">
          <div className="breadcrumb">
            <button
              className={`breadcrumb-item ${!currentFolderId ? "active" : ""}`}
              onClick={handleRootClick}
            >
              <BiHome size={16} />
              Root
            </button>
            {getBreadcrumbPath().map((folder, index, array) => (
              <React.Fragment key={folder._id}>
                <BiChevronRight size={14} />
                <button
                  className={`breadcrumb-item ${index === array.length - 1 ? "active" : ""}`}
                  onClick={() => handleFolderClick(folder._id)}
                  disabled={index === array.length - 1}
                >
                  {folder.name}
                </button>
              </React.Fragment>
            ))}
          </div>
          <span className="total-items">{mydocs.total || 0} items total</span>
        </div>
      </div>

      {/* Tree View */}
      <div className={directoryTree.length === 0 ? "" : "documents-tree"}>
        {directoryTree.length === 0 ? (
          <div className="no-data-container">
            <h3 className="no-data-title">No Documents Found</h3>
            <p className="no-data-message">
              You haven't uploaded any documents yet. Click "Add Document" or
              "New Folder" to get started.
            </p>
          </div>
        ) : (
          <div className="tree-container">
            <div className="tree-header">
              <div className="tree-column name-column">Name</div>
              <div className="tree-column date-column">Date Modified</div>
              <div className="tree-column size-column">Size</div>
              <div className="tree-column actions-column">Actions</div>
            </div>
            <div className="tree-content">{renderTreeItems(directoryTree)}</div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-backdrop">
          <div className="modal" ref={createModalRef}>
            <div className="modal-header">
              <h3>
                {createType === "folder"
                  ? "Create New Folder"
                  : "Add New Document"}
              </h3>
              <button className="close-btn" onClick={handleCloseCreateModal}>
                ×
              </button>
            </div>

            <div className="add-doc-form">
              <div className="form-group">
                <label>Court Reference</label>
                <input
                  type="text"
                  value={courtRef || ""}
                  readOnly
                  className="court-ref-input"
                />
              </div>

              <div className="form-group">
                <label>Create in</label>
                <div className="location-input-container">
                  <BiHome size={16} />
                  <input
                    type="text"
                    value={getCurrentFolderName()}
                    readOnly
                    className="location-input"
                  />
                  {currentFolderId && (
                    <button
                      type="button"
                      onClick={handleRootClick}
                      className="root-btn"
                      title="Move to Root"
                    >
                      <BiHome size={14} />
                    </button>
                  )}
                </div>
              </div>

              {createType === "folder" ? (
                <div className="form-group">
                  <label>Folder Name</label>
                  <input
                    type="text"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    placeholder="Enter folder name"
                    className="folder-name-input"
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>Upload Document(s)</label>
                  <div className="upload-section">
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="file-input"
                      disabled={uploading}
                      accept={getAcceptString()}
                      multiple
                      title="Allowed formats: PDF, DOC, DOCX, TXT, PNG, JPEG, JPG, XLS, XLSX"
                    />
                    <div className="file-format-info">
                      <small>
                        Allowed formats: PDF, DOC, DOCX, TXT, PNG, JPEG, JPG, XLS, XLSX
                        (Max 50MB)
                      </small>
                    </div>
                    {/* Progress for Single File Upload */}
                    {!batchProgress.total && uploading && (
                      <div className="upload-progress">
                        <div className="progress-bar">
                          <div
                            className="progress"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <span>{Math.round(uploadProgress)}% uploaded</span>
                      </div>
                    )}

                    {/* Progress for Batch Upload */}
                    {batchProgress.total > 0 && (
                       <div className="upload-progress batch-progress">
                         <div className="batch-status-text">
                            Uploading {batchProgress.current}/{batchProgress.total} files
                         </div>
                         <div className="progress-bar">
                            <div 
                              className="progress" 
                              style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                            />
                         </div>
                       </div>
                    )}

                    {uploadedUrl && selectedFile && !batchProgress.total && (
                      <div className="uploaded-file-preview">
                        <div className="file-info">
                          <BiFile size={16} />
                          <span>{selectedFile.name}</span>
                          <button
                            type="button"
                            onClick={handleCancelUpload}
                            className="cancel-upload-button"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCloseCreateModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="submit-btn"
                  disabled={
                    isSubmitting ||
                    uploading ||
                    (createType === "file" && !uploadedUrl && batchProgress.total === 0) ||
                    (createType === "folder" && !folderName.trim())
                  }
                  onClick={
                    createType === "folder"
                      ? handleCreateFolder
                      : handleCreateFile
                  }
                >
                  {isSubmitting
                    ? createType === "folder"
                      ? "Creating..."
                      : "Adding..."
                    : uploading
                      ? batchProgress.total > 0 ? "Uploading..." : "Uploading..."
                      : createType === "folder"
                        ? "Create Folder"
                        : "Add Document"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        itemType={deleteModal.docName}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};